import Keycloak from 'keycloak-js';

const keycloakConfig = {
  url: process.env.REACT_APP_KEYCLOAK_URL || 'http://localhost:8081',
  realm: process.env.REACT_APP_KEYCLOAK_REALM || 'ai-leadgen',
  clientId: process.env.REACT_APP_KEYCLOAK_CLIENT_ID || 'ai-leadgen-frontend',
};

const keycloak = new Keycloak(keycloakConfig);

// Manually set authServerUrl since Keycloak constructor doesn't set it properly
keycloak.authServerUrl = keycloakConfig.url;
keycloak.realm = keycloakConfig.realm;
keycloak.clientId = keycloakConfig.clientId;

// Track token expiration
let tokenExpirationTime = null;
let refreshTimer = null;

// Calculate when to refresh token (5 minutes before expiry)
const calculateRefreshTime = (expiresIn) => {
  // Refresh 5 minutes (300 seconds) before expiry, but at least after 30 seconds
  const refreshBuffer = Math.min(300, Math.max(30, expiresIn - 300));
  return (expiresIn - refreshBuffer) * 1000;
};

// Setup automatic token refresh
const setupTokenRefresh = (expiresIn) => {
  // Clear existing timer
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }

  if (!keycloak.authenticated || !keycloak.refreshToken) {
    return;
  }

  const refreshTime = calculateRefreshTime(expiresIn);
  tokenExpirationTime = Date.now() + expiresIn * 1000;

  refreshTimer = setTimeout(async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || '/api';

      const response = await fetch(`${apiUrl}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          refresh_token: keycloak.refreshToken,
        }),
      });

      if (response.ok) {
        const tokens = await response.json();

        // Update tokens
        keycloak.token = tokens.access_token;
        keycloak.refreshToken = tokens.refresh_token;
        keycloak.idToken = tokens.id_token;

        // Decode new token
        if (tokens.access_token) {
          const base64Url = tokens.access_token.split('.')[1];
          const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
          keycloak.tokenParsed = JSON.parse(atob(base64));
        }

        // Update stored tokens in appropriate storage
        const rememberMe = localStorage.getItem('remember_me') === 'true';
        const expiresIn = tokens.expires_in || 300;
        const now = Date.now();

        if (rememberMe) {
          const sessionExpiry = localStorage.getItem('session_expiry');

          // Check if 30-day session is still valid
          if (sessionExpiry && parseInt(sessionExpiry) > now) {
            localStorage.setItem(
              'keycloak_tokens',
              JSON.stringify({
                access_token: tokens.access_token,
                refresh_token: tokens.refresh_token,
                id_token: tokens.id_token,
                expires_at: now + expiresIn * 1000,
              })
            );
            localStorage.setItem('last_activity', now.toString());
          } else {
            keycloak.authenticated = false;
            localStorage.removeItem('keycloak_tokens');
            localStorage.removeItem('remember_me');
            localStorage.removeItem('session_expiry');
            localStorage.removeItem('session_start');
            localStorage.removeItem('last_activity');
            window.location.href =
              '/login?message=' +
              encodeURIComponent('Your session has expired. Please login again.');
            return;
          }
        } else if (sessionStorage.getItem('keycloak_tokens')) {
          // Update sessionStorage for temporary sessions to survive F5
          sessionStorage.setItem(
            'keycloak_tokens',
            JSON.stringify({
              access_token: tokens.access_token,
              refresh_token: tokens.refresh_token,
              id_token: tokens.id_token,
              expires_at: now + expiresIn * 1000,
            })
          );
          sessionStorage.setItem('last_activity', now.toString());
          console.log('Temporary session tokens updated in sessionStorage');
        }

        // Setup next refresh
        setupTokenRefresh(tokens.expires_in || 300);

        // Emit event for other components to update
        window.dispatchEvent(new CustomEvent('keycloak-token-refreshed'));
      } else {
        console.warn('⚠️ Background token refresh failed. Will retry on next API call.');
        // Don't redirect – the axios interceptor will handle recovery on the next 401
      }
    } catch (error) {
      console.warn('⚠️ Error during background token refresh:', error.message);
      // Don't redirect – let the user keep working; axios will refresh on next call
    }
  }, refreshTime);
};

// Expose method to start token refresh
keycloak.startTokenRefresh = (expiresIn) => {
  setupTokenRefresh(expiresIn);
};

// Expose method to stop token refresh
keycloak.stopTokenRefresh = () => {
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
  tokenExpirationTime = null;
};

// Callback for auth state changes (called by authService.login)
keycloak.onAuthSuccess = null; // Will be set by KeycloakContext

// Notify listeners that auth state changed
keycloak.notifyAuthSuccess = () => {
  if (typeof keycloak.onAuthSuccess === 'function') {
    keycloak.onAuthSuccess();
  }
  window.dispatchEvent(new CustomEvent('keycloak-auth-success'));
};

export default keycloak;
